import React, { useState, useEffect } from 'react';
import { Download, ChevronDown, ChevronUp, FileText, GraduationCap, BookOpen, Book, Search, X, Users, GitMerge, School, BookOpen as BookOpenIcon, ChevronDownCircle, ChevronUpCircle } from 'lucide-react';
import { useResources } from '../../contexts/ResourcesContext';
import EmailModal from './EmailModal';
import { Resource } from '../../types/resource';
import { downloadPdf } from '../../services/resourceService';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

// Labels pour les chapitres de Terminale
const chaptersTerminale: Record<string, string> = {
  'croissance': 'Croissance économique',
  'commerce': 'Commerce international',
  'chomage': 'Chômage',
  'crise': 'Crises financières',
  'europe': 'Europe',
  'structure': 'Structure sociale',
  'ecole': 'École',
  'mobilite': 'Mobilité sociale',
  'travail': 'Travail-Emploi',
  'engagement': 'Engagement politique',
  'justice': 'Justice sociale',
  'environnement': 'Environnement'
};

// Labels pour les chapitres de Première
const chaptersPremiere: Record<string, string> = {
  'marche1': 'Comment un marché concurrentiel fonctionne-t-il ?',
  'marche2': 'Comment les marchés imparfaitement concurrentiels fonctionnent-ils ?',
  'marche3': 'Quelles sont les principales défaillances du marché ?',
  'financement': 'Comment les agents économiques se financent-ils ?',
  'monnaie': 'Qu\'est-ce que la monnaie et comment est-elle créée ?',
  'socialisation': 'Comment la socialisation contribue-t-elle à expliquer les différences de comportement des individus ?',
  'lien': 'Comment se construisent et évoluent les liens sociaux ?',
  'deviance': 'Quels sont les processus sociaux qui contribuent à la déviance ?',
  'opinion': 'Comment se forme et s\'exprime l\'opinion publique ?',
  'vote': 'Voter : une affaire individuelle ou collective ?',
  'protection-sociale': 'Comment l\'assurance et la protection sociale contribuent-elles à la gestion des risques dans les sociétés développées ?',
  'entreprise': 'Comment les entreprises sont-elles organisées et gouvernées ?'
};

// Labels pour les chapitres de Seconde
const chaptersSeconde: Record<string, string> = {
  'intro': 'Comment les économistes, les sociologues et les politistes raisonnent-ils et travaillent-ils ?',
  'richesse': 'Comment crée-t-on des richesses et comment les mesure-t-on ?',
  'marche': 'Comment se forment les prix sur un marché ?',
  'acteurs-sociaux': 'Comment devenons-nous des acteurs sociaux ?',
  'vie-politique': 'Comment s\'organise la vie politique ?',
  'relations': 'Quelles relations entre le diplôme, l\'emploi et le salaire ?'
};

// Chapitres par niveau et thème
const chaptersMap = {
  'terminale': {
    'economie': ['croissance', 'commerce', 'chomage', 'crise', 'europe'],
    'sociologie': ['structure', 'ecole', 'mobilite', 'travail', 'engagement'],
    'regards-croises': ['environnement'],
    'methode-bac': ['methode-bac'] // Utiliser un chapitre générique pour methode-bac
  },
  'premiere': {
    'economie': ['marche1', 'marche2', 'marche3', 'financement', 'monnaie'],
    'sociologie': ['socialisation', 'lien', 'deviance', 'opinion', 'vote'],
    'regards-croises': ['protection-sociale', 'entreprise']
  },
  'seconde': {
    'introduction': ['intro'],
    'economie': ['richesse', 'marche'],
    'sociologie': ['acteurs-sociaux', 'vie-politique'],
    'regards-croises': ['relations']
  }
};

// Fonction pour obtenir le label du chapitre en fonction du niveau
const getChapterLabel = (chapter: string, level: string): string => {
  if (level === 'terminale') {
    return chaptersTerminale[chapter] || chapter;
  } else if (level === 'premiere') {
    return chaptersPremiere[chapter] || chapter;
  } else if (level === 'seconde') {
    return chaptersSeconde[chapter] || chapter;
  }
  return chapter;
};

// Labels pour les thèmes
const themeLabels: Record<string, string> = {
  'economie': 'Économie',
  'sociologie': 'Sociologie',
  'regards-croises': 'Regards Croisés',
  'methode-bac': 'Méthode Bac',
  'introduction': 'Introduction'
};

// Couleurs par niveau - tous les niveaux utilisent la même couleur (celle de Terminale)
const levelColors: Record<string, { bg: string, border: string, icon: string, text: string }> = {
  'terminale': {
    bg: 'bg-indigo-50',
    border: 'border-indigo-300',
    icon: 'text-indigo-500',
    text: 'text-indigo-800'
  },
  'premiere': {
    bg: 'bg-indigo-50',
    border: 'border-indigo-300',
    icon: 'text-indigo-500',
    text: 'text-indigo-800'
  },
  'seconde': {
    bg: 'bg-indigo-50',
    border: 'border-indigo-300',
    icon: 'text-indigo-500',
    text: 'text-indigo-800'
  }
};

// Couleurs par thème
const themeColors: Record<string, { bg: string, border: string, icon: string, text: string }> = {
  'economie': {
    bg: 'bg-purple-50',
    border: 'border-purple-300',
    icon: 'text-purple-500',
    text: 'text-purple-800'
  },
  'sociologie': {
    bg: 'bg-purple-50',
    border: 'border-purple-300',
    icon: 'text-purple-500',
    text: 'text-purple-800'
  },
  'regards-croises': {
    bg: 'bg-purple-50',
    border: 'border-purple-300',
    icon: 'text-purple-500',
    text: 'text-purple-800'
  },
  'methode-bac': {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    icon: 'text-emerald-500',
    text: 'text-emerald-800'
  },
  'introduction': {
    bg: 'bg-purple-50',
    border: 'border-purple-300',
    icon: 'text-purple-500',
    text: 'text-purple-800'
  }
};

// Couleurs par type de ressource - ADOUCIES
const typeColors: Record<string, { bg: string, border: string, icon: string, text: string, button: string, buttonHover: string }> = {
  'fiche': {
    bg: 'bg-blue-50',
    border: 'border-blue-300',
    icon: 'text-blue-500',
    text: 'text-blue-800',
    button: 'bg-blue-400',
    buttonHover: 'hover:bg-blue-500'
  },
  'plan': {
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    icon: 'text-amber-500',
    text: 'text-amber-800',
    button: 'bg-amber-400',
    buttonHover: 'hover:bg-amber-500'
  },
  'generale': {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    icon: 'text-emerald-500',
    text: 'text-emerald-800',
    button: 'bg-emerald-400',
    buttonHover: 'hover:bg-emerald-500'
  },
  'ec1': {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    icon: 'text-emerald-500',
    text: 'text-emerald-800',
    button: 'bg-emerald-400',
    buttonHover: 'hover:bg-emerald-500'
  },
  'ec2': {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    icon: 'text-emerald-500',
    text: 'text-emerald-800',
    button: 'bg-emerald-400',
    buttonHover: 'hover:bg-emerald-500'
  },
  'ec3': {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    icon: 'text-emerald-500',
    text: 'text-emerald-800',
    button: 'bg-emerald-400',
    buttonHover: 'hover:bg-emerald-500'
  },
  'dissertation': {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    icon: 'text-emerald-500',
    text: 'text-emerald-800',
    button: 'bg-emerald-400',
    buttonHover: 'hover:bg-emerald-500'
  }
};

// Définir l'ordre d'affichage des thèmes
const themeOrder = ['economie', 'sociologie', 'regards-croises', 'introduction', 'methode-bac'];

// Définir l'ordre d'affichage des niveaux
const levelOrder = ['terminale', 'premiere', 'seconde'];

// Labels pour les types de ressources
const typeLabels: Record<string, string> = {
  'fiche': 'Fiche récap\'',
  'plan': 'Plan type',
  'generale': 'Méthode générale',
  'ec1': 'Méthode EC1',
  'ec2': 'Méthode EC2',
  'ec3': 'Méthode EC3',
  'dissertation': 'Méthode Dissertation'
};

const ResourcesList = () => {
  const {
    resources,
    filteredResources,
    loading,
    selectedType,
    setSelectedType,
    selectedLevel,
    setSelectedLevel,
    selectedTheme,
    setSelectedTheme,
    searchQuery,
    setSearchQuery
  } = useResources();

  const { currentUser } = useAuth();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  
  // États pour les éléments dépliés - tout est replié par défaut
  const [expandedLevels, setExpandedLevels] = useState<string[]>([]);
  const [expandedThemes, setExpandedThemes] = useState<string[]>([]);
  const [expandedChapters, setExpandedChapters] = useState<string[]>([]);

  // Fonction pour basculer l'état d'expansion d'un niveau
  const toggleLevel = (level: string) => {
    setExpandedLevels(prev => 
      prev.includes(level)
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  };

  // Fonction pour basculer l'état d'expansion d'un thème
  const toggleTheme = (theme: string, level: string) => {
    const key = `${level}-${theme}`;
    setExpandedThemes(prev => 
      prev.includes(key)
        ? prev.filter(t => t !== key)
        : [...prev, key]
    );
  };

  // // Fonction pour basculer l'état d'expansion d'un chapitre
  const toggleChapter = (chapter: string, theme: string, level: string) => {
    const key = `${level}-${theme}-${chapter}`;
    setExpandedChapters(prev => 
      prev.includes(key)
        ? prev.filter(c => c !== key)
        : [...prev, key]
    );
  };

  // Fonction pour tout déplier
  const expandAll = () => {
    // Déplier tous les niveaux
    setExpandedLevels([...levelOrder]);
    
    // Déplier tous les thèmes
    const allThemeKeys: string[] = [];
    levelOrder.forEach(level => {
      themeOrder.forEach(theme => {
        // Ignorer le thème Introduction pour les niveaux autres que Seconde
        if (!(theme === 'introduction' && level !== 'seconde')) {
          allThemeKeys.push(`${level}-${theme}`);
        }
      });
    });
    setExpandedThemes(allThemeKeys);
    
    // Déplier tous les chapitres
    const allChapterKeys: string[] = [];
    levelOrder.forEach(level => {
      themeOrder.forEach(theme => {
        // Ignorer le thème Introduction pour les niveaux autres que Seconde
        if (!(theme === 'introduction' && level !== 'seconde')) {
          if (chaptersMap[level]?.[theme]) {
            chaptersMap[level][theme].forEach(chapter => {
              allChapterKeys.push(`${level}-${theme}-${chapter}`);
            });
          }
        }
      });
    });
    setExpandedChapters(allChapterKeys);
  };

  // Fonction pour tout replier
  const collapseAll = () => {
    setExpandedLevels([]);
    setExpandedThemes([]);
    setExpandedChapters([]);
  };

  // Compter les ressources par niveau
  const countResourcesByLevel = (level: string) => {
    return resources.filter(r => r.level === level).length;
  };

  // Compter les ressources par thème dans un niveau
  const countResourcesByTheme = (level: string, theme: string) => {
    return resources.filter(r => r.level === level && r.theme === theme).length;
  };

  // Compter les ressources par chapitre
  const countResourcesByChapter = (level: string, theme: string, chapter: string) => {
    return resources.filter(r => r.level === level && r.theme === theme && r.chapter === chapter).length;
  };

  // Obtenir l'icône du thème
  const getThemeIcon = (theme: string) => {
    switch (theme) {
      case 'economie':
        return <Book className={themeColors[theme].icon} size={18} />;
      case 'sociologie':
        return <Users className={themeColors[theme].icon} size={18} />;
      case 'regards-croises':
        return <GitMerge className={themeColors[theme].icon} size={18} />;
      case 'methode-bac':
        return <GraduationCap className={themeColors[theme].icon} size={18} />;
      case 'introduction':
        return <BookOpenIcon className={themeColors[theme].icon} size={18} />;
      default:
        return <Book className={themeColors.economie.icon} size={18} />;
    }
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSelectedType('all');
    setSelectedTheme('all');
    setSearchQuery('');
  };

  // Vérifier si des filtres sont actifs
  const hasActiveFilters = selectedType !== 'all' || selectedTheme !== 'all' || searchQuery !== '';

  // Fonction pour obtenir l'icône de la ressource
  const getResourceIcon = (resource: Resource) => {
    // Si c'est une ressource de méthode bac, utiliser des icônes spécifiques
    if (resource.theme === 'methode-bac') {
      switch (resource.type) {
        case 'generale':
          return <GraduationCap className="text-emerald-500" size={20} />;
        case 'ec1':
          return <School className="text-emerald-500" size={20} />;
        case 'ec2':
          return <GitMerge className="text-emerald-500" size={20} />;
        case 'ec3':
          return <Users className="text-emerald-500" size={20} />;
        case 'dissertation':
          return <BookOpen className="text-emerald-500" size={20} />;
        default:
          return <GraduationCap className="text-emerald-500" size={20} />;
      }
    }
    
    // Pour les autres types de ressources
    switch (resource.type) {
      case 'fiche':
        return <FileText className={typeColors.fiche.icon} size={20} />;
      case 'plan':
        return <BookOpen className={typeColors.plan.icon} size={20} />;
      case 'generale':
        return <GraduationCap className={typeColors.generale.icon} size={20} />;
      case 'ec1':
        return <GraduationCap className={typeColors.ec1.icon} size={20} />;
      case 'ec2':
        return <GraduationCap className={typeColors.ec2.icon} size={20} />;
      case 'ec3':
        return <GraduationCap className={typeColors.ec3.icon} size={20} />;
      case 'dissertation':
        return <GraduationCap className={typeColors.dissertation.icon} size={20} />;
      default:
        return <FileText className={typeColors.fiche.icon} size={20} />;
    }
  };

  // Fonction pour obtenir les couleurs en fonction du type de ressource
  const getResourceColors = (resource: Resource) => {
    // Si c'est une ressource de méthode bac, utiliser les couleurs vertes
    if (resource.theme === 'methode-bac') {
      return typeColors.generale; // Toujours utiliser les couleurs vertes pour méthode-bac
    }
    
    // Sinon, utiliser les couleurs normales par type
    return typeColors[resource.type] || typeColors.fiche;
  };

  const handleDownload = async (resource: Resource) => {
    try {
      // Vérifier si l'utilisateur est connecté
      if (!currentUser || !currentUser.emailVerified) {
        // Si l'utilisateur n'est pas connecté ou son email n'est pas vérifié, ouvrir la modal
        setSelectedResourceId(resource.id);
        setIsEmailModalOpen(true);
        return;
      }
      
      // Si l'utilisateur est connecté et son email est vérifié, procéder au téléchargement
      const fileName = `${resource.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      await downloadPdf(resource.pdfUrl, fileName);
      toast.success('Téléchargement lancé !');
    } catch (error) {
      console.error("Error during download:", error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  return (
    <>
      {/* Barre de recherche et boutons d'expansion */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-2 border-[#9a6ebd]">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une ressource..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-[#9a6ebd] text-[#323c5f] placeholder-gray-400 focus:ring-[#9a6ebd] focus:border-[#9a6ebd]"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#9a6ebd] text-white hover:bg-[#8a5dad] transition-colors"
            >
              <ChevronDownCircle size={20} />
              <span>Tout déplier</span>
            </button>
            <button
              onClick={collapseAll}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              <ChevronUpCircle size={20} />
              <span>Tout replier</span>
            </button>
          </div>
        </div>
      </div>

      {resources.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-lg border-2 border-[#9a6ebd]">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Search className="text-gray-400" size={24} />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucune ressource trouvée</h3>
          <p className="text-gray-500">Essayez de modifier vos critères de recherche.</p>
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="mt-4 text-[#9a6ebd] hover:text-[#8a5dad] transition-colors"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Structure hiérarchique des ressources */}
          {levelOrder.map(level => {
            const levelResources = countResourcesByLevel(level);
            
            return (
              <div key={level} className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-indigo-300">
                {/* En-tête du niveau */}
                <button
                  onClick={() => toggleLevel(level)}
                  className={`w-full flex items-center justify-between p-4 ${levelColors[level].bg} border-b-2 ${levelColors[level].border} transition-colors hover:bg-opacity-80`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${levelColors[level].bg} border-2 ${levelColors[level].border} flex items-center justify-center`}>
                      <School className={levelColors[level].icon} size={20} />
                    </div>
                    <h2 className={`text-xl font-bold ${levelColors[level].text}`}>
                      {level === 'terminale' ? 'Terminale' : level === 'premiere' ? 'Première' : 'Seconde'}
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({levelResources} ressource{levelResources > 1 ? 's' : ''})
                      </span>
                    </h2>
                  </div>
                  {expandedLevels.includes(level) ? (
                    <ChevronUp className={levelColors[level].icon} size={24} />
                  ) : (
                    <ChevronDown className={levelColors[level].icon} size={24} />
                  )}
                </button>

                {/* Contenu du niveau */}
                {expandedLevels.includes(level) && (
                  <div className="p-4">
                    <div className="space-y-4">
                      {/* Thèmes dans ce niveau */}
                      {themeOrder.map(theme => {
                        // Ignorer le thème Introduction pour les niveaux autres que Seconde
                        if (theme === 'introduction' && level !== 'seconde') return null;
                        
                        // Ignorer le thème Méthode Bac pour Première et Seconde
                        if (theme === 'methode-bac' && level !== 'terminale') return null;
                        
                        const themeKey = `${level}-${theme}`;
                        const themeResources = countResourcesByTheme(level, theme);
                        
                        return (
                          <div key={theme} className="ml-4 border-l-2 border-gray-300 pl-4">
                            {/* En-tête du thème */}
                            <button
                              onClick={() => toggleTheme(theme, level)}
                              className={`w-full flex items-center justify-between p-3 ${themeColors[theme].bg} rounded-lg border-2 ${themeColors[theme].border} transition-colors hover:bg-opacity-80`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="md:inline hidden">{getThemeIcon(theme)}</span>
                                <h3 className={`font-semibold ${themeColors[theme].text} text-left`}>
                                  {themeLabels[theme]}
                                  <span className="ml-2 text-sm font-normal text-gray-500">
                                    ({themeResources} ressource{themeResources > 1 ? 's' : ''})
                                  </span>
                                </h3>
                              </div>
                              {expandedThemes.includes(themeKey) ? (
                                <ChevronUp className={themeColors[theme].icon} size={20} />
                              ) : (
                                <ChevronDown className={themeColors[theme].icon} size={20} />
                              )}
                            </button>

                            {/* Contenu du thème */}
                            {expandedThemes.includes(themeKey) && (
                              <div className="mt-2 space-y-3">
                                {/* Traitement spécial pour le thème Méthode Bac */}
                                {theme === 'methode-bac' ? (
                                  <div className="ml-4 border-l-2 border-gray-300 pl-4 space-y-3">
                                    {/* Afficher les ressources de méthode-bac avec code couleur par type */}
                                    {resources
                                      .filter(r => r.level === level && r.theme === 'methode-bac')
                                      .map(resource => {
                                        // Toujours utiliser les couleurs vertes pour méthode-bac
                                        const colors = typeColors.generale;
                                        return (
                                          <div 
                                            key={resource.id}
                                            className={`${colors.bg} rounded-lg shadow-sm border-2 ${colors.border} hover:shadow-md transition-shadow overflow-hidden`}
                                          >
                                            <div className="p-3">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="md:inline hidden">{getResourceIcon(resource)}</span>
                                                <h6 className={`font-medium ${colors.text} text-sm md:text-base`}>
                                                  {resource.title}
                                                </h6>
                                              </div>
                                              
                                              {/* Description avec meilleure intégration visuelle */}
                                              <div className="bg-white/60 p-3 rounded-lg border-2 border-emerald-200 mb-3">
                                                <p className="text-sm text-gray-700">
                                                  {resource.description}
                                                </p>
                                              </div>
                                              
                                              {/* Bouton télécharger */}
                                              <button
                                                onClick={() => handleDownload(resource)}
                                                className={`w-full flex items-center justify-center gap-2 px-3 py-2 ${colors.button} text-white rounded-lg ${colors.buttonHover} transition-colors`}
                                              >
                                                <Download size={16} />
                                                <span>Télécharger</span>
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })
                                    }
                                    
                                    {/* Message si aucune ressource */}
                                    {resources.filter(r => r.level === level && r.theme === 'methode-bac').length === 0 && (
                                      <div className="p-4 bg-gray-50 rounded-lg text-center border-2 border-gray-200">
                                        <p className="text-gray-500 text-sm">Aucune ressource disponible pour le moment</p>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  // Affichage normal pour les autres thèmes
                                  chaptersMap[level]?.[theme]?.map(chapter => {
                                    const chapterKey = `${level}-${theme}-${chapter}`;
                                    const chapterResources = countResourcesByChapter(level, theme, chapter);
                                    
                                    return (
                                      <div key={chapter} className="ml-4 border-l-2 border-gray-300 pl-4">
                                        {/* En-tête du chapitre */}
                                        <button
                                          onClick={() => toggleChapter(chapter, theme, level)}
                                          className="w-full flex items-center justify-between p-2 bg-gray-50 rounded-lg border-2 border-gray-300 hover:bg-gray-100 transition-colors"
                                        >
                                          <div className="flex items-center gap-2">
                                            <div className="text-left">
                                              <h4 className="font-medium text-gray-800 text-sm md:text-base">
                                                {getChapterLabel(chapter, level)}
                                              </h4>
                                              <span className="text-xs text-gray-500 md:hidden">
                                                {chapterResources} ressource{chapterResources > 1 ? 's' : ''}
                                              </span>
                                              <span className="hidden md:block text-sm font-normal text-gray-500">
                                                {chapterResources} ressource{chapterResources > 1 ? 's' : ''}
                                              </span>
                                            </div>
                                          </div>
                                          {expandedChapters.includes(chapterKey) ? (
                                            <ChevronUp className="text-gray-600 flex-shrink-0" size={18} />
                                          ) : (
                                            <ChevronDown className="text-gray-600 flex-shrink-0" size={18} />
                                          )}
                                        </button>

                                        {/* Contenu du chapitre */}
                                        {expandedChapters.includes(chapterKey) && (
                                          <div className="mt-2 space-y-3">
                                            {/* Afficher les ressources du chapitre avec code couleur par type */}
                                            {resources
                                              .filter(r => r.level === level && r.theme === theme && r.chapter === chapter)
                                              .map(resource => {
                                                const colors = getResourceColors(resource);
                                                return (
                                                  <div 
                                                    key={resource.id}
                                                    className={`${colors.bg} rounded-lg shadow-sm border-2 ${colors.border} hover:shadow-md transition-shadow overflow-hidden`}
                                                  >
                                                    <div className="p-3">
                                                      <div className="flex items-center gap-2 mb-2">
                                                        <span className="md:inline hidden">{getResourceIcon(resource)}</span>
                                                        <h6 className={`font-medium ${colors.text} text-sm md:text-base`}>
                                                          {resource.title}
                                                        </h6>
                                                      </div>
                                                      
                                                      {/* Badge du type de ressource */}
                                                      <div className="mb-3">
                                                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
                                                          {typeLabels[resource.type] || resource.type}
                                                        </span>
                                                      </div>
                                                      
                                                      {/* Description avec meilleure intégration visuelle */}
                                                      <div className="bg-white/60 p-3 rounded-lg border-2 border-gray-200 mb-3">
                                                        <p className="text-sm text-gray-700">
                                                          {resource.description}
                                                        </p>
                                                      </div>
                                                      
                                                      {/* Bouton télécharger */}
                                                      <button
                                                        onClick={() => handleDownload(resource)}
                                                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 ${colors.button} text-white rounded-lg ${colors.buttonHover} transition-colors`}
                                                      >
                                                        <Download size={16} />
                                                        <span>Télécharger</span>
                                                      </button>
                                                    </div>
                                                  </div>
                                                );
                                              })
                                            }
                                            
                                            {/* Message si aucune ressource */}
                                            {resources.filter(r => r.level === level && r.theme === theme && r.chapter === chapter).length === 0 && (
                                              <div className="p-4 bg-gray-50 rounded-lg text-center border-2 border-gray-200">
                                                <p className="text-gray-500 text-sm">Aucune ressource disponible pour le moment</p>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        resourceId={selectedResourceId}
      />
    </>
  );
};

export default ResourcesList;